import React from 'react'
import { TouchableOpacity, View } from 'react-native'
import { useTheme } from '@ui-kitten/components'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Icon, IconName, Radio, Text } from '@src/components/base'
import { useIsDarkMode } from '@src/config/theme'

export interface ChartSelectionCardProps {
  title: string
  description: string
  iconName: IconName
  variant: ChartSelectionVariant
  onPress: () => void
  selected: boolean
}

export type ChartSelectionVariant = 'glucose' | 'dial' | 'nutrition' | 'none'

export const ChartSelectionCard = ({
  title,
  description,
  iconName,
  variant,
  onPress,
  selected,
}: ChartSelectionCardProps) => {
  const styles = useStyleSheet(themedStyle)
  const isDarkMode = useIsDarkMode()
  const theme = useTheme()
  const iconCircleBackgroundColor = isDarkMode
    ? theme['theme.surface.base1']
    : theme['theme.surface.base3']

  return (
    <TouchableOpacity
      disabled={selected}
      onPress={onPress}
      accessibilityLabel={`${title} Card Preview`}
    >
      <View style={[styles.innerContainer, selected && styles.selected]}>
        <View style={[styles.iconCircle, { backgroundColor: iconCircleBackgroundColor }]}>
          <Icon name={iconName} style={[styles[`${variant}Variant`], styles.icon]} />
        </View>
        <View style={styles.contentContainer}>
          <Text type="large" bold>
            {title}
          </Text>
          <Text type="regular">{description}</Text>
        </View>
        <Radio checked={selected} style={styles.radio} onPress={onPress} />
      </View>
    </TouchableOpacity>
  )
}

const themedStyle = StyleService.create({
  innerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
    borderWidth: 1,
    borderRadius: 16,
    backgroundColor: 'theme.background',
    borderColor: 'theme.surface.base1',
    padding: 16,
  },
  iconCircle: {
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 32,
    width: 52,
    height: 52,
    marginRight: 12,
  },
  selected: {
    borderColor: 'theme.primary.base',
  },
  glucoseVariant: {
    color: 'theme.success.light_',
  },
  dialVariant: {
    color: 'theme.info.base',
  },
  nutritionVariant: {
    color: 'theme.warning.base',
  },
  noneVariant: {
    color: 'theme.error.base',
  },
  icon: {
    width: 32,
    height: 32,
  },
  contentContainer: {
    flex: 1,
  },
  radio: {
    alignSelf: 'center',
    justifyContent: 'center',
    marginLeft: 12,
  },
})
